import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import gameService from "../services/gameService";
import { getProjectNames } from "../services/projectService";
import projectImageService from "../services/projectImageService";
import fileUpload from "../utils/fileUpload";

const PAGE_SIZE = 200;

const GameUploadNewVersion = () => {
  const [loading, setLoading] = useState(false);
  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [projectNames, setProjectNames] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [inlineEditorOpen, setInlineEditorOpen] = useState({});
  const [inlineEditorFiles, setInlineEditorFiles] = useState({});
  const [inlineEditorUploading, setInlineEditorUploading] = useState({});
  const [inlineInputKeys, setInlineInputKeys] = useState({});

  // Extract unique providers and categories from games
  const providers = useMemo(() => {
    const unique = new Set(games.map((g) => g.provider?.name || "Unknown"));
    return Array.from(unique).sort();
  }, [games]);

  const categories = useMemo(() => {
    const unique = new Set(games.map((g) => g.category?.name || "Uncategorized"));
    return Array.from(unique).sort();
  }, [games]);

  // Filter games based on search, provider, and category
  useEffect(() => {
    let filtered = games;
    if (searchTerm) {
      filtered = filtered.filter((g) =>
        (g.name || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (selectedProvider) {
      filtered = filtered.filter((g) => g.provider?.name === selectedProvider);
    }
    if (selectedCategory) {
      filtered = filtered.filter((g) => g.category?.name === selectedCategory);
    }
    setFilteredGames(filtered);
    setCurrentPage(1);
  }, [games, searchTerm, selectedProvider, selectedCategory]);

  const cardShellStyle = {
    border: "1px solid #e0e7ff",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 15px 30px rgba(15, 23, 42, 0.08)",
    transition: "transform 0.2s, box-shadow 0.2s",
  };

  const metaPillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 999,
    background: "#f3f4f6",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3,
    color: "#4b5563",
    textTransform: "uppercase",
  };

  const metaDotStyle = (color) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: color,
  });

  const projectGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  };

  const projectPlaceholderStyle = {
    width: "100%",
    height: 95,
    borderRadius: 8,
    border: "1px dashed #d1d5db",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f9fafb",
    color: "#6b7280",
    fontSize: 12,
    cursor: "pointer",
    transition: "background-color 0.2s",
  };

  const chipStyle = {
    width: 32,
    height: 32,
    borderRadius: 10,
    background: "#eef2ff",
    color: "#4338ca",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
  };

  const idBadgeStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: "#475467",
    background: "#f9fafb",
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid #e4e7ec",
    letterSpacing: 0.3,
  };

  const getInitial = (value) =>
    value && typeof value === "string" && value.trim().length > 0
      ? value.trim().charAt(0).toUpperCase()
      : "?";

  const fetchAllData = useCallback(async (showLoader = true) => {
    if (showLoader) {
      setLoading(true);
    }
    try {
      const [gameResult, projectResult] = await Promise.allSettled([
        gameService.getGames(),
        getProjectNames(),
      ]);

      if (gameResult.status !== "fulfilled") {
        throw gameResult.reason || new Error("Unable to load game data");
      }

      const gameResponse = gameResult.value;
      const data = Array.isArray(gameResponse?.data?.data)
        ? gameResponse.data.data
        : [];
      data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setGames(data);
      setFilteredGames(data);

      if (projectResult.status === "fulfilled") {
        const pnResponse = projectResult.value;
        const pnData = Array.isArray(pnResponse?.data?.data)
          ? pnResponse.data.data
          : [];
        pnData.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        setProjectNames(pnData);
      } else {
        setProjectNames([]);
        console.warn("Project names failed to load", projectResult.reason);
      }
    } catch (e) {
      console.error("loadAll error", e);
      toast.error("Unable to load games", { position: "top-right" });
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, []);

  const getInlineEditorKey = (gameId, identifier) => `${gameId}-${identifier}`;

  const clearInlineEditorState = (editorKey) => {
    setInlineEditorFiles((prev) => ({ ...prev, [editorKey]: null }));
    setInlineInputKeys((prev) => ({ ...prev, [editorKey]: Date.now() }));
  };

  const toggleInlineEditor = (editorKey) => {
    setInlineEditorOpen((prev) => {
      const next = !prev[editorKey];
      if (!next) {
        clearInlineEditorState(editorKey);
      }
      return { ...prev, [editorKey]: next };
    });
  };

  const handleInlineFileChange = (editorKey, file) => {
    setInlineEditorFiles((prev) => ({ ...prev, [editorKey]: file || null }));
  };

  const handleInlineProjectImageUpload = async (gameId, projectId, editorKey) => {
    if (!projectId) {
      toast.error("Missing project reference", { position: "top-right" });
      return;
    }
    const file = inlineEditorFiles[editorKey];
    if (!file) {
      toast.error("Select an image", { position: "top-right" });
      return;
    }
    try {
      setInlineEditorUploading((prev) => ({ ...prev, [editorKey]: true }));
      const uploadedPath = await fileUpload(file);
      await projectImageService.upsertProjectImage({
        projectName: projectId,
        image: uploadedPath,
        gameId,
      });
      toast.success("Image updated", { position: "top-right" });
      clearInlineEditorState(editorKey);
      setInlineEditorOpen((prev) => ({ ...prev, [editorKey]: false }));
      await fetchAllData(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Image update failed",
        { position: "top-right" }
      );
    } finally {
      setInlineEditorUploading((prev) => ({ ...prev, [editorKey]: false }));
    }
  };

  const handleDrop = (editorKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      handleInlineFileChange(editorKey, file);
    } else {
      toast.error("Only image files are allowed", { position: "top-right" });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const renderProjectGallery = (game) => {
    const projectImages = Array.isArray(game.projectImageDocs)
      ? game.projectImageDocs
      : [];

    const projectImageMap = new Map();
    projectImages.forEach((pi, idx) => {
      const rawId =
        typeof pi?.projectName === "object"
          ? pi?.projectName?._id
          : pi?.projectName;
      if (rawId) {
        projectImageMap.set(String(rawId), pi);
      } else if (pi?._id) {
        projectImageMap.set(String(pi._id), pi);
      } else {
        projectImageMap.set(`fallback-${idx}`, pi);
      }
    });

    const galleryItems =
      projectNames.length > 0
        ? projectNames.map((pn) => {
            const match = projectImageMap.get(String(pn._id));
            return {
              key: pn._id,
              title: pn.title || "Unnamed",
              projectId: pn._id,
              imageUrl: match?.image
                ? `${process.env.REACT_APP_API_URL}/${match.image}`
                : null,
            };
          })
        : projectImages.map((pi, idx) => {
            const title = pi?.projectName?.title || "Unnamed";
            const fallbackProjectId =
              typeof pi?.projectName === "object"
                ? pi?.projectName?._id
                : pi?.projectName || null;
            return {
              key: pi?._id || `${game._id}-pi-${idx}`,
              title,
              projectId: fallbackProjectId,
              imageUrl: pi?.image
                ? `${process.env.REACT_APP_API_URL}/${pi.image}`
                : null,
            };
          });

    if (galleryItems.length === 0) {
      return (
        <div className="mt-2" style={projectGridStyle}>
          <div className="text-center" title="No project data">
            <div style={projectPlaceholderStyle}>Empty</div>
            <div className="small mt-1" style={{ color: "#6b7280" }}>
              No project data
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-2" style={projectGridStyle}>
        {galleryItems.map(({ key, title, imageUrl, projectId }) => {
          const projectIdentifier = projectId || key;
          const editorKey = getInlineEditorKey(game._id, projectIdentifier);
          const isInlineEditing = !!inlineEditorOpen[editorKey];
          const inlineFile = inlineEditorFiles[editorKey];
          const inlineBusy = !!inlineEditorUploading[editorKey];
          const shortTitle = title.length > 20 ? `${title.slice(0, 20)}…` : title;
          return (
            <div key={key} className="text-center" title={title}>
              <div
                onClick={() => !imageUrl && toggleInlineEditor(editorKey)}
                onDrop={(e) => isInlineEditing && handleDrop(editorKey, e)}
                onDragOver={handleDragOver}
                style={{
                  ...projectPlaceholderStyle,
                  backgroundColor: isInlineEditing ? "#e5e7eb" : "#f9fafb",
                }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={title}
                    style={{
                      width: "100%",
                      height: 95,
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #d1d5db",
                    }}
                  />
                ) : isInlineEditing ? (
                  <span>Drop image here or click to upload</span>
                ) : (
                  <span>No image</span>
                )}
              </div>
              <div className="small mt-1 fw-medium" style={{ color: "#374151" }}>
                {shortTitle}
              </div>
              {projectId && (
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-decoration-underline"
                    onClick={() => toggleInlineEditor(editorKey)}
                  >
                    {isInlineEditing ? "Cancel" : imageUrl ? "Replace image" : "Upload image"}
                  </button>
                  {isInlineEditing && (
                    <div className="mt-2 text-start">
                      <input
                        key={inlineInputKeys[editorKey] || `inline-${editorKey}`}
                        type="file"
                        accept="image/*"
                        className="form-control form-control-sm mb-2"
                        onChange={(e) =>
                          handleInlineFileChange(editorKey, e.target.files?.[0] || null)
                        }
                      />
                      {inlineFile && (
                        <div className="small text-muted mb-2">
                          Selected: {inlineFile.name}
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary btn-sm w-100"
                        onClick={() =>
                          handleInlineProjectImageUpload(game._id, projectId, editorKey)
                        }
                        disabled={inlineBusy || !inlineFile}
                      >
                        {inlineBusy ? "Uploading..." : "Save Image"}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {!projectId && (
                <div className="small text-danger mt-1">Project ID missing</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const totalPages = Math.max(1, Math.ceil(filteredGames.length / PAGE_SIZE));
  const paginatedGames = filteredGames.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, filteredGames.length);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div>
      <Header />
      <div className="container py-5">
        <ToastContainer />
        <div className="card shadow-lg p-4" style={{ borderRadius: 16 }}>
          <h2 className="text-center mb-4 fw-bold">All Games Management</h2>
          <div className="row mb-4 g-3">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by game name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
              >
                <option value="">All Providers</option>
                {providers.map((prov) => (
                  <option key={prov} value={prov}>
                    {prov}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {loading && (
            <div className="alert alert-info text-center">লোড হচ্ছে...</div>
          )}
          {!loading && filteredGames.length === 0 && (
            <div className="alert alert-warning text-center">কোনো গেম পাওয়া যায়নি।</div>
          )}
          {!loading && filteredGames.length > 0 && (
            <>
              <div className="row g-4">
                {paginatedGames.map((g) => (
                  <div key={g._id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                    <div
                      className="card h-100"
                      style={cardShellStyle}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-5px)";
                        e.currentTarget.style.boxShadow = "0 20px 40px rgba(15, 23, 42, 0.12)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 15px 30px rgba(15, 23, 42, 0.08)";
                      }}
                    >
                      <div className="position-relative" style={{ height: 180, overflow: "hidden" }}>
                        {g.image ? (
                          <img
                            src={`${process.env.REACT_APP_API_URL}/${g.image}`}
                            alt={g.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <div
                            className="d-flex align-items-center justify-content-center bg-light"
                            style={{ width: "100%", height: "100%" }}
                          >
                            <span className="text-muted">No image</span>
                          </div>
                        )}
                        <div
                          style={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            background: "rgba(15, 23, 42, 0.65)",
                            color: "#fff",
                            fontSize: 11,
                            padding: "4px 10px",
                            borderRadius: 999,
                            letterSpacing: 0.3,
                            textTransform: "uppercase",
                          }}
                        >
                          #{g.game_uuid?.slice(-6) || "ID"}
                        </div>
                      </div>
                      <div className="card-body d-flex flex-column p-3">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div style={chipStyle}>{getInitial(g.name)}</div>
                          <span style={idBadgeStyle}>{g.game_uuid || "Unassigned"}</span>
                        </div>
                        <h6 className="fw-bold text-dark mb-2">
                          {g.name || "Untitled game"}
                        </h6>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <span style={metaPillStyle}>
                            <span style={metaDotStyle("#6366f1")} />
                            {g.provider?.name || "Unknown provider"}
                          </span>
                          <span style={metaPillStyle}>
                            <span style={metaDotStyle("#22c55e")} />
                            {g.category?.name || "Uncategorized"}
                          </span>
                        </div>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <span className="small text-uppercase text-muted fw-semibold">
                            Project Gallery
                          </span>
                        </div>
                        {renderProjectGallery(g)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 mt-4">
                <div className="small text-muted">
                  মোট {filteredGames.length} গেম • পেজ {currentPage}/{totalPages}
                  {paginatedGames.length > 0 && (
                    <span>
                      {" "}| দেখানো হচ্ছে {startIndex + 1}-{endIndex}
                    </span>
                  )}
                </div>
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)}>
                        প্রথম
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}>
                        আগের
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}>
                        পরের
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)}>
                        শেষ
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameUploadNewVersion;